import React, { useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Btn, Modal, Notice, Label } from "./ui/index.jsx";
import { ENTRY_KINDS } from "../lib/useComms.js";

/* What the reading found, before anybody has agreed to it.
 *
 * Nothing here has been saved. Every line arrives ticked because most of them
 * will be right, and every one can be reworded, moved to a different box or
 * untucked and dropped. The sentence it came from sits underneath it so the
 * check takes a second rather than a trip back to the document.
 *
 * This screen is the whole point of the design. The report goes to a client
 * under Witt O'Brien's name and nothing reaches it that a person has not
 * looked at.
 */

const input = {
  width: "100%", padding: "6px 8px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 13, background: "#fff", color: T.ink,
};

export default function ProposalReview({
  summary, entries, workstreams = [], label = "Workstream",
  sourceNote = "", documentId = null, busy, onClose, onSave,
}) {
  const [rows, setRows] = useState(
    entries.map((e, i) => ({ ...e, _key: i, _keep: true })),
  );
  const set = (key, patch) =>
    setRows(rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const keeping = rows.filter((r) => r._keep && r.title.trim());
  const allOn = rows.length > 0 && rows.every((r) => r._keep);

  const save = () =>
    onSave(keeping.map((r) => ({
      kind: r.kind,
      workstream: r.workstream || null,
      title: r.title.trim(),
      quote: r.quote || null,
      source_note: r.source_note || sourceNote || null,
      document_id: documentId,
      confirmed_at: new Date().toISOString(),
    })));

  return (
    <Modal title="What the document said" onClose={onClose} wide>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.65 }}>
          Nothing below has been saved yet. Reword anything that does not read
          the way you would say it, move a line to a different box or
          {" "}{label.toLowerCase()} if it belongs somewhere else, and untick
          anything that should not be in the report.
        </div>

        {summary && <Notice kind="info">{summary}</Notice>}

        {rows.length === 0 ? (
          <Notice kind="warn">
            Nothing in that document read like a report line. It may be a cover
            sheet or an agenda. Add the points by hand if there are any.
          </Notice>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Label color={GOLD_INK}>{keeping.length} of {rows.length} kept</Label>
              <Btn small kind="ghost"
                onClick={() => setRows(rows.map((r) => ({ ...r, _keep: !allOn })))}>
                {allOn ? "Untick all" : "Tick all"}
              </Btn>
            </div>

            <div style={{ display: "grid", gap: 8, maxHeight: "52vh", overflowY: "auto", paddingRight: 4 }}>
              {rows.map((r) => (
                <div key={r._key} style={{
                  display: "grid", gridTemplateColumns: "22px minmax(0, 1fr)", gap: 10,
                  padding: "9px 10px", background: r._keep ? T.chip : "transparent",
                  border: `1px solid ${T.line}`, borderRadius: 4,
                  opacity: r._keep ? 1 : 0.55,
                }}>
                  <input type="checkbox" checked={r._keep} style={{ marginTop: 8 }}
                    onChange={(e) => set(r._key, { _keep: e.target.checked })} />

                  <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                    <input style={input} value={r.title}
                      onChange={(e) => set(r._key, { title: e.target.value })} />

                    <div style={{ display: "grid", gap: 6,
                                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
                      <select style={input} value={r.kind}
                        onChange={(e) => set(r._key, { kind: e.target.value })}>
                        {ENTRY_KINDS.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
                      </select>
                      <select style={input} value={r.workstream || ""}
                        onChange={(e) => set(r._key, { workstream: e.target.value })}>
                        <option value="">No {label.toLowerCase()}</option>
                        {workstreams.map((w) => <option key={w} value={w}>{w}</option>)}
                      </select>
                    </div>

                    {r.quote && (
                      <div style={{ fontSize: 11.5, color: T.soft, fontStyle: "italic", lineHeight: 1.55 }}>
                        "{r.quote}"
                        {r.source_note && (
                          <span style={{ fontStyle: "normal" }}> · {r.source_note}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Not now</Btn>
          <Btn disabled={busy || keeping.length === 0} onClick={save}>
            {busy ? "Saving..." : `Keep ${keeping.length} line${keeping.length === 1 ? "" : "s"}`}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
